import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import pdfWorker from 'react-pdf/node_modules/pdfjs-dist/build/pdf.worker.min.mjs?url';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { isAdmin, isAssistant } from '../../utils/permissions';
import { listUsers } from '../../services/userManagementService';
import { uploadFiles, listInbox, linkToInvoice, linkToExpense, triggerExtraction } from '../../services/invoiceInboxService';
import { subInvoiceService } from '../../services/subInvoiceService';
import { supabase } from '../../lib/supabase';

import Icon from '../../components/AppIcon';
import useTranslation from '../../hooks/useTranslation';
import InvoiceForm from './components/InvoiceForm';
import StatsCard from '../../components/ui/StatsCard';
import NativePdfViewer from './components/NativePdfViewer';
import { getCachedSignedUrl } from '../../utils/signedUrlCache';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { formatDate as formatLocaleDate } from '../../utils/formatDate';
import { formatCurrency as fmtCAD } from '../../utils/formatCurrency';
import { statusVariant } from '../../utils/statusVariant';
import Badge from '../../components/ui/Badge';
import BulkActionBar from '../../components/ui/BulkActionBar';

// Sanitize filenames for storage keys: strip diacritics, replace invalid chars, keep spaces/._-
const safeName = (name) =>
  // replace remaining invalid chars
  // strip diacritics: é→e, ç→c, à→a
  (name || '')?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '')?.replace(/[^a-zA-Z0-9 ._-]/g, '_')?.trim();

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

/**
 * Une facture a-t-elle deja ete analysee par l'IA ?
 *
 * Source UNIQUE de la reponse : le badge « Analyse », l'etat desactive du bouton
 * « Scan IA » et le filtre du scan groupe la partagent. Trois lectures de la meme
 * verite — les recopier serait trois occasions de les laisser diverger.
 */
const isAnalyzed = (it) =>
  it?.aiProcessed === true
  && it?.extractedData
  && Object.keys(it?.extractedData || {})?.length > 0;

/**
 * Le montant d'une facture, quel que soit le champ ou l'IA l'a range.
 *
 * `amount` n'est qu'un alias de `subtotal`. Une facture sans ligne « Sous-total »
 * mais avec un grand total n'a donc que `totalAmount` — et affichait « — » alors
 * que la valeur etait en base. `??` et non `||` : 0 est un montant, pas un vide.
 *
 * Lu par la colonne Montant ET par buildInitialValues : le formulaire et la liste
 * doivent montrer le meme chiffre.
 */
const extractedAmount = (item) => {
  const ext = item?.extractedData || {};
  return ext?.amount ?? ext?.subtotal ?? ext?.totalAmount ?? null;
};

// L'erreur de l'IA doit tenir dans un toast sans le faire deborder.
const truncateError = (msg, max = 160) => {
  const s = String(msg || '')?.trim();
  return s?.length > max ? `${s?.slice(0, max)}...` : s;
};

const STATUS_CONFIG = {
  new: { labelKey: 'invoiceInbox.filter.new', bg: 'bg-blue-100', text: 'text-blue-600', dot: 'bg-blue-500' },
  ready: { labelKey: 'invoiceInbox.filter.ready', bg: 'bg-purple-100', text: 'text-purple-600', dot: 'bg-purple-500' },
  created: { labelKey: 'invoiceInbox.filter.created', bg: 'bg-green-100', text: 'text-green-600', dot: 'bg-green-500' },
};

const StatusPill = ({ status, t }) => {
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.new;
  return (
    <Badge variant={statusVariant('invoiceInbox', status)}>
      {t(cfg?.labelKey)}
    </Badge>
  );
};

// ─── PDF Skeleton Placeholder ─────────────────────────────────
const PdfSkeleton = ({ t }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full overflow-hidden relative flex flex-col">
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#F8F9FA] z-10">
      <div className="w-8 h-8 border-2 border-gray-200 border-t-primary rounded-full animate-spin" />
      <p className="text-sm text-gray-500">{t('invoiceInbox.loadingDocument')}</p>
    </div>
    {/* Skeleton lines to give visual structure */}
    <div className="flex-1 overflow-hidden p-6 pt-16 opacity-10">
      <div className="space-y-3">
        {[100, 80, 90, 60, 75, 85, 70, 95, 65, 80]?.map((w, i) => (
          <div key={i} className="h-3 bg-gray-300 rounded animate-pulse" style={{ width: `${w}%` }} />
        ))}
      </div>
    </div>
  </div>
);

// ─── Upload Progress Banner ───────────────────────────────────
const UploadProgressBanner = ({ progress, rejections, onDismiss }) => {
  const { t } = useTranslation();
  const { current, total, accepted, rejected } = progress;
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  const isDone = current >= total;

  return (
    <div className={`relative rounded-xl border p-4 mb-4 transition-all ${isDone ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
      {isDone && (
        <button onClick={onDismiss} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition" aria-label="Dismiss">✕</button>
      )}
      <div className="flex items-center justify-between mb-2 pr-6">
        <div className="flex items-center gap-2">
          {isDone ? (
            <Icon name="CheckCircle" size={16} className="text-green-500" />
          ) : (
            <div className="w-4 h-4 border-2 border-orange-300 border-t-primary rounded-full animate-spin" />
          )}
          <span className="text-sm font-medium text-gray-700">
            {isDone ? t('invoiceInbox.upload.done') : t('invoiceInbox.upload.inProgress')}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1 text-green-600 font-medium"><Icon name="Check" size={12} />{t('invoiceInbox.upload.accepted', { count: accepted })}</span>
          {rejected > 0 && (
            <span className="flex items-center gap-1 text-red-500 font-medium"><Icon name="X" size={12} />{t('invoiceInbox.upload.rejected', { count: rejected })}</span>
          )}
        </div>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
        <div className={`h-1.5 rounded-full transition-all duration-300 ${isDone ? 'bg-green-500' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-gray-500">
        {isDone
          ? [
              // Le pluriel se decide en i18n, jamais par une concatenation
              // anglaise. Meme paire singulier/pluriel que invoiceCount.
              accepted === 1
                ? t('invoiceInbox.upload.addedOne')
                : t('invoiceInbox.upload.added', { count: accepted }),
              rejected > 0
                ? (rejected === 1
                    ? t('invoiceInbox.upload.skippedOne')
                    : t('invoiceInbox.upload.skipped', { count: rejected }))
                : null,
            ]?.filter(Boolean)?.join(', ')
          : t('invoiceInbox.upload.processing', { current, total })}
      </p>
      {rejections?.length > 0 && (
        <div className="mt-3 space-y-1">
          <p className="text-xs font-semibold text-red-500 uppercase tracking-wide">{t('invoiceInbox.upload.skippedTitle')}</p>
          {rejections?.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
              <Icon name="FileX" size={12} className="text-red-400 flex-shrink-0" />
              <span className="truncate max-w-[240px] font-medium">{r?.name}</span>
              <span className="text-red-400">
                — {t(`invoiceInbox.upload.reason.${r?.reason}`) !== `invoiceInbox.upload.reason.${r?.reason}`
                    ? t(`invoiceInbox.upload.reason.${r?.reason}`)
                    : r?.reason}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────
const InvoiceInboxPage = () => {
  const { t, language } = useTranslation();
  const { userProfile } = useAuth();
  // Seuls l'administrateur et l'adjoint voient les factures d'autrui : le
  // filtre n'a de sens que pour eux. Les autres n'ont qu'une vue, la leur.
  const canFilterByUploader = isAdmin(userProfile) || isAssistant(userProfile);
  const myId = userProfile?.id || null;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('inbox');
  // 'me' plutot que l'id brut : au premier rendu userProfile n'est pas encore
  // charge. Le sentinel est resolu en id au moment de la requete.
  const [filterUploader, setFilterUploader] = useState('me');
  const [search, setSearch] = useState('');
  const [selectedInboxItem, setSelectedInboxItem] = useState(null);
  const [successItemId, setSuccessItemId] = useState(null);
  const fileInputRef = useRef(null);
  const [statusDropOpen, setStatusDropOpen] = useState(false);
  const [uploaderDropOpen, setUploaderDropOpen] = useState(false);
  const statusDropRef = useRef(null);
  const uploaderDropRef = useRef(null);

  // ─── Loading state for row click ─────────────────────────
  const [openingItemId, setOpeningItemId] = useState(null);

  // ─── Pre-fetched signed URL for current item ──────────────
  const [currentSignedUrl, setCurrentSignedUrl] = useState(null);

  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadRejections, setUploadRejections] = useState([]);

  // ─── Bulk selection state ─────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  // scanState: Map<id, 'scanning' | 'ready'>
  const [scanState, setScanState] = useState(new Map());
  // scannedData: Map<id, extractedData>
  const [scannedData, setScannedData] = useState(new Map());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // ─── Retry AI extraction state ────────────────────────────
  const [retryingIds, setRetryingIds] = useState(new Set());
  // retryToast: { type: 'loading' | 'success' | 'error', message }
  const [retryToast, setRetryToast] = useState(null);
  const retryToastSeq = useRef(0);
  // Facture deja analysee dont on attend la confirmation avant de repayer. null = pas de dialogue.
  const [rescanConfirmItem, setRescanConfirmItem] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const { listInbox: listFn } = await import('../../services/invoiceInboxService');
      // 'me' -> l'id reel. 'all' reste 'all' : listInbox l'ignore.
      const uploaderId = filterUploader === 'me' ? (myId || 'all') : filterUploader;
      // Les tuiles comptent CE QUE LA LISTE MONTRE. Sans ce filtre, un admin
      // qui regarde Pedro verrait la liste de Pedro au-dessus des totaux de
      // toute l'organisation — deux chiffres qui se contredisent.
      const allData = await listFn({ status: 'all', search: '', uploaderId });
      setAllItems(allData);
      // Fetch filtered items for display
      let data;
      if (filterStatus === 'inbox') {
        // Show new + ready (exclude created)
        const [newItems, readyItems] = await Promise.all([
          listFn({ status: 'new', search, uploaderId }),
          listFn({ status: 'ready', search, uploaderId }),
        ]);
        data = [...newItems, ...readyItems]?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      } else {
        data = await listFn({ status: filterStatus, search, uploaderId });
      }
      setItems(data);
    } catch (err) {
      console.error('Failed to load inbox:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, search, filterUploader, myId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // ─── Close dropdowns on outside click ────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (statusDropRef?.current && !statusDropRef?.current?.contains(e?.target)) {
        setStatusDropOpen(false);
      }
      if (uploaderDropRef?.current && !uploaderDropRef?.current?.contains(e?.target)) {
        setUploaderDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ─── Pre-warm signed URLs for all visible items ───────────
  useEffect(() => {
    if (!items?.length) return;
    // Pre-generate signed URLs for all non-created items in background
    const active = items?.filter(i => i?.status !== 'created' && i?.filePath);
    active?.forEach(item => {
      getCachedSignedUrl(item?.filePath, 3600)?.catch(() => {});
    });
  }, [items]);

  // ─── Pre-fetch adjacent PDFs when viewing an invoice ─────
  const prefetchAdjacentRef = useRef(null);
  useEffect(() => {
    if (!selectedInboxItem) return;

    // Cancel any pending prefetch
    if (prefetchAdjacentRef?.current) {
      clearTimeout(prefetchAdjacentRef?.current);
    }

    // Delay slightly so current PDF loads first
    prefetchAdjacentRef.current = setTimeout(() => {
      const navigable = items?.filter(i => i?.status !== 'created');
      const idx = navigable?.findIndex(i => i?.id === selectedInboxItem?.id);

      const toPreload = [];
      if (idx > 0 && navigable?.[idx - 1]?.filePath) toPreload?.push(navigable?.[idx - 1]?.filePath);
      if (idx < navigable?.length - 1 && navigable?.[idx + 1]?.filePath) toPreload?.push(navigable?.[idx + 1]?.filePath);

      toPreload?.forEach(fp => {
        getCachedSignedUrl(fp, 3600)?.catch(() => {});
      });
    }, 800);

    return () => {
      if (prefetchAdjacentRef?.current) clearTimeout(prefetchAdjacentRef?.current);
    };
  }, [selectedInboxItem?.id, items]);

  // ─── Checkbox helpers ─────────────────────────────────────
  const selectableItems = useMemo(() => items?.filter(i => i?.status !== 'created'), [items]);
  const allSelected = selectableItems?.length > 0 && selectableItems?.every(i => selectedIds?.has(i?.id));
  const someSelected = selectedIds?.size > 0;
  const isIndeterminate = someSelected && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableItems?.map(i => i?.id)));
    }
  };

  const toggleSelectItem = (e, id) => {
    e?.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  };

  // ─── Bulk Delete ──────────────────────────────────────────
  // Memes deux corrections que deleteInboxItem : la ligne AVANT le fichier, et
  // un DELETE sans effet est rendu visible. `.select()` renvoie les lignes
  // reellement supprimees — sans lui, un refus de la RLS passait pour un
  // succes et le PDF partait quand meme.
  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    setDeleteError(null);
    try {
      const idsToDelete = Array.from(selectedIds);
      const itemsToDelete = items?.filter(i => idsToDelete?.includes(i?.id));

      // 1. Les lignes d'abord, en reclamant celles qui sont vraiment parties.
      const { data: deletedRows, error: dbError } = await supabase
        ?.from('invoice_inbox')
        ?.delete()
        ?.in('id', idsToDelete)
        ?.select('id');
      if (dbError) throw new Error(dbError?.message);

      const deletedIds = new Set((deletedRows || [])?.map(r => r?.id));
      const failedIds = idsToDelete?.filter(id => !deletedIds?.has(id));

      // 2. Les fichiers ensuite, et UNIQUEMENT ceux dont la ligne est partie.
      const filePaths = itemsToDelete
        ?.filter(i => deletedIds?.has(i?.id))
        ?.map(i => i?.filePath)
        ?.filter(Boolean);
      if (filePaths?.length > 0) {
        await supabase?.storage?.from('invoice-inbox')?.remove(filePaths);
      }

      // 3. L'ecran ne perd que ce que la base a confirme.
      setItems(prev => prev?.filter(i => !deletedIds?.has(i?.id)));
      setAllItems(prev => prev?.filter(i => !deletedIds?.has(i?.id)));
      setSelectedIds(new Set(failedIds));   // les echecs restent selectionnes
      setScanState(prev => {
        const next = new Map(prev);
        deletedIds?.forEach(id => next?.delete(id));
        return next;
      });
      setScannedData(prev => {
        const next = new Map(prev);
        deletedIds?.forEach(id => next?.delete(id));
        return next;
      });

      if (failedIds?.length > 0) {
        setDeleteError(
          deletedIds?.size === 0
            ? t('invoiceInbox.deleteErrors.notPermitted')
            : t('invoiceInbox.deleteErrors.partial', {
                deleted: deletedIds?.size,
                failed: failedIds?.length,
              })
        );
      }
    } catch (err) {
      console.error('Bulk delete failed:', err);
      setDeleteError(t('invoiceInbox.deleteErrors.failed'));
    } finally {
      setBulkDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // ─── Bulk AI Pre-scan ─────────────────────────────────────
  const scanningIds = useMemo(() => {
    const s = new Set();
    scanState?.forEach((v, k) => { if (v === 'scanning') s?.add(k); });
    return s;
  }, [scanState]);

  const handleBulkScan = async () => {
    const idsToScan = Array.from(selectedIds)?.filter(id => {
      const st = scanState?.get(id);
      if (st === 'scanning' || st === 'ready') return false;
      // scanState est une Map de session, vide apres tout rechargement : sans ce
      // test, une facture analysee hier repartirait chez OpenAI.
      return !isAnalyzed(items?.find(i => i?.id === id));
    });
    if (!idsToScan?.length) {
      // Un clic qui ne fait rien sans un mot, c'est ce qui a masque la panne d'extraction.
      const seq = ++retryToastSeq.current;
      setRetryToast({ type: 'success', message: 'Toutes les factures sélectionnées sont déjà analysées.' });
      setTimeout(() => { if (retryToastSeq?.current === seq) setRetryToast(null); }, 4000);
      return;
    }

    // Mark all as scanning
    setScanState(prev => {
      const next = new Map(prev);
      idsToScan?.forEach(id => next?.set(id, 'scanning'));
      return next;
    });

    // Run scans with limited concurrency (max 5 simultaneous)
    const CONCURRENCY = 5;
    const results = [];

    for (let i = 0; i < idsToScan?.length; i += CONCURRENCY) {
      const batch = idsToScan?.slice(i, i + CONCURRENCY);
      const batchResults = await Promise.allSettled(
        batch?.map(async (id) => {
          try {
            const result = await triggerExtraction(id);
            // result = { success: true, extracted: {...} } or { success: false, error: '...' }
            const extracted = result?.extracted || null;
            const success = result?.success === true && extracted && extracted?.vendorName;

            if (success) {
              // Update local state with extracted data from Edge Function response
              const mergedExtracted = { ...extracted };
              const suggestedType = extracted?.documentType || null;

              setScannedData(prev => new Map(prev)?.set(id, mergedExtracted));
              setItems(prev => prev?.map(i => i?.id === id
                ? { ...i, extractedData: { ...(i?.extractedData || {}), ...mergedExtracted }, aiSuggestedType: suggestedType || i?.aiSuggestedType, status: 'ready', aiProcessed: true }
                : i
              ));
              setAllItems(prev => prev?.map(i => i?.id === id
                ? { ...i, extractedData: { ...(i?.extractedData || {}), ...mergedExtracted }, aiSuggestedType: suggestedType || i?.aiSuggestedType, status: 'ready', aiProcessed: true }
                : i
              ));
              setScanState(prev => { const n = new Map(prev); n?.set(id, 'ready'); return n; });
            } else {
              // Extraction failed or returned empty — do NOT set 'ready'
              // Edge Function already persisted ai_processed=false + ai_last_error in DB.
              // Keep its diagnostic on the row: it is the only thing that explains the failure.
              const errMsg = result?.error || 'Extraction echouee';
              setScanState(prev => { const n = new Map(prev); n?.set(id, 'error'); return n; });
              setItems(prev => prev?.map(i => i?.id === id ? { ...i, aiProcessed: false, aiLastError: errMsg } : i));
              setAllItems(prev => prev?.map(i => i?.id === id ? { ...i, aiProcessed: false, aiLastError: errMsg } : i));
            }
          } catch (err) {
            console.error(`Scan failed for ${id}:`, err);
            const errMsg = err instanceof Error ? err?.message : String(err);
            setScanState(prev => { const n = new Map(prev); n?.set(id, 'error'); return n; });
            setItems(prev => prev?.map(i => i?.id === id ? { ...i, aiProcessed: false, aiLastError: errMsg } : i));
            setAllItems(prev => prev?.map(i => i?.id === id ? { ...i, aiProcessed: false, aiLastError: errMsg } : i));
          }
        })
      );
      results?.push(...batchResults);
    }
  };

  // ─── Retry AI extraction handler ─────────────────────────
  const handleRetryExtraction = async (e, item) => {
    e?.stopPropagation();
    if (retryingIds?.has(item?.id)) return;

    const seq = ++retryToastSeq.current;
    setRetryingIds(prev => new Set(prev)?.add(item?.id));
    setRetryToast({ type: 'loading', message: 'Extraction en cours...' });

    // Ce bouton est desormais le SEUL chemin qui efface les marqueurs de matching.
    // L'effacement doit preceder l'appel : l'Edge Function ecrit extracted_data en
    // retour, et le formulaire relit ses gardes des la reouverture du modal. Sans
    // ca, un sous-traitant ajoute apres une premiere tentative ne serait plus
    // jamais rapproche.
    const clearedMarkers = {
      supplierMatchAttemptedAt: null,
      costCodeMatchAttemptedAt: null,
      matchedSubcontractorId: null,
      costCodeSource: null,
    };
    try {
      const { updateExtractedData } = await import('../../services/invoiceInboxService');
      await updateExtractedData(item?.id, clearedMarkers);
      setItems(prev => prev?.map(i => i?.id === item?.id
        ? { ...i, extractedData: { ...(i?.extractedData || {}), ...clearedMarkers } }
        : i));
      setAllItems(prev => prev?.map(i => i?.id === item?.id
        ? { ...i, extractedData: { ...(i?.extractedData || {}), ...clearedMarkers } }
        : i));
      setSelectedInboxItem(prev => (prev?.id === item?.id
        ? { ...prev, extractedData: { ...(prev?.extractedData || {}), ...clearedMarkers } }
        : prev));
    } catch (err) {
      console.error('Failed to clear match markers:', err);
    }

    let result = null;
    let thrown = null;
    try {
      result = await triggerExtraction(item?.id);
    } catch (err) {
      console.error('Retry extraction failed:', err);
      thrown = err;
    }

    // Same success contract as the bulk scan: the Edge Function answers HTTP 200
    // even when extraction failed, so success lives in the payload, not the status.
    const extracted = result?.extracted || null;
    const success = result?.success === true && extracted && extracted?.vendorName;

    if (success) {
      setRetryToast({ type: 'success', message: 'Extraction reussie' });
    } else {
      const errMsg = result?.error
        || (thrown instanceof Error ? thrown?.message : null)
        || 'Extraction echouee';
      setRetryToast({ type: 'error', message: `Echec : ${truncateError(errMsg)}` });
    }

    // The Edge Function has returned and is the sole writer — reload now, not on a timer.
    try {
      const { getById } = await import('../../services/invoiceInboxService');
      const updated = await getById(item?.id);
      setItems(prev => prev?.map(i => i?.id === item?.id ? { ...i, ...updated } : i));
      setAllItems(prev => prev?.map(i => i?.id === item?.id ? { ...i, ...updated } : i));
    } catch (err) {
      console.error('Failed to refresh row after retry:', err);
    }

    setRetryingIds(prev => {
      const next = new Set(prev);
      next?.delete(item?.id);
      return next;
    });

    // Leave a failure on screen long enough to be read; never clear a newer toast.
    setTimeout(() => {
      if (retryToastSeq?.current === seq) setRetryToast(null);
    }, success ? 3000 : 9000);
  };

  const handleRowClick = async (item) => {
    if (item?.status === 'created') return;

    // Show instant loading feedback
    setOpeningItemId(item?.id);

    // Start fetching signed URL immediately (may already be cached)
    const urlPromise = item?.filePath ? getCachedSignedUrl(item?.filePath, 3600)?.catch(() => null) : Promise.resolve(null);

    // For all non-created items: fetch URL in parallel, open immediately
    const url = await urlPromise;
    setCurrentSignedUrl(url);
    setOpeningItemId(null);
    setSelectedInboxItem(item);
  };

  const handleUpload = async (files) => {
    if (!files?.length) return;
    const total = files?.length;
    setUploadProgress({ current: 0, total, accepted: 0, rejected: 0 });
    setUploadRejections([]);
    const { accepted, rejected } = await uploadFiles(files, (prog) => { setUploadProgress(prog); });
    setUploadRejections(rejected);
    await fetchItems();
    setTimeout(() => { setUploadProgress(null); setUploadRejections([]); }, 5000);
  };

  const handleFileInput = (e) => {
    handleUpload(e?.target?.files);
    e.target.value = '';
  };

  // Le helper central : il suit la langue et porte la garde T12:00:00 sans
  // laquelle une date nue recule d'un jour au Quebec.
  const formatDate = (dateStr) => formatLocaleDate(dateStr, language);

  const getExtractedField = (item, field) => item?.extractedData?.[field] || '—';

  const buildInitialValues = useCallback((item) => {
    const ext = item?.extractedData || {};
    return {
      invoiceNumber: ext?.invoiceNumber || '',
      invoiceDate: ext?.invoiceDate || ext?.date || '',
      receivedDate: ext?.receivedDate || '',
      // Meme cascade que la colonne Montant : sans elle le montant n'arrivait
      // meme pas dans le formulaire.
      amount: extractedAmount(item) ?? '',
      supplier: ext?.supplier || '',
    };
  }, []);

  const selectedInboxInitialValues = useMemo(
    () => (selectedInboxItem ? buildInitialValues(selectedInboxItem) : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedInboxItem?.id, selectedInboxItem?.extractedData]
  );

  // ─── Navigation: only active inbox items (new + ready) ──
  const navigableItems = useMemo(
    () => items?.filter(i => i?.status !== 'created'),
    [items]
  );

  const currentNavIndex = useMemo(
    () => navigableItems?.findIndex(i => i?.id === selectedInboxItem?.id),
    [navigableItems, selectedInboxItem?.id]
  );

  const remainingCount = useMemo(
    () => navigableItems?.length,
    [navigableItems]
  );

  const handleNavPrev = useCallback(async () => {
    if (currentNavIndex <= 0) return;
    const prevItem = navigableItems?.[currentNavIndex - 1];
    if (prevItem) await handleRowClick(prevItem);
  }, [currentNavIndex, navigableItems]);

  const handleNavNext = useCallback(async () => {
    if (currentNavIndex >= navigableItems?.length - 1) return;
    const nextItem = navigableItems?.[currentNavIndex + 1];
    if (nextItem) await handleRowClick(nextItem);
  }, [currentNavIndex, navigableItems]);

  const handleFormSubmit = async (payload, lines) => {
    const inv = await subInvoiceService?.create(payload, lines);

    const filePath = selectedInboxItem?.filePath;
    const fileName = selectedInboxItem?.fileName;
    if (filePath && fileName) {
      try {
        // Step 1: Download from invoice-inbox
        const { data: fileData, error: downloadError } = await supabase?.storage
          ?.from('invoice-inbox')
          ?.download(filePath);
        if (downloadError || !fileData) {
          console.error('[handleFormSubmit] Download failed — keeping original in invoice-inbox:', downloadError);
          // Do NOT delete original; facture linked but file stays in inbox for cascade fallback
        } else {
          // Step 2: Upload to sub-invoices (NEW canonical bucket) at {projectId}/{subInvoiceId}/{safeName}
          const factureProjectId = payload?.projectId || inv?.projectId;
          const safeFileName = safeName(fileName);
          const newPath = `${factureProjectId}/${inv?.id}/${safeFileName}`;
          const { data: uploadData, error: uploadError } = await supabase?.storage
            ?.from('sub-invoices')
            ?.upload(newPath, fileData, { contentType: 'application/pdf', upsert: true });

          // Step 2b: Also upload to contracts-documents for transition safety (fallback readers)
          const legacyPath = `sub_invoices/${inv?.id}/${safeFileName}`;
          await supabase?.storage
            ?.from('contracts-documents')
            ?.upload(legacyPath, fileData, { contentType: 'application/pdf', upsert: true })
            ?.catch(e => console.warn('[handleFormSubmit] Legacy contracts-documents upload failed (non-fatal):', e));

          if (uploadError) {
            console.error('[handleFormSubmit] Upload to sub-invoices failed — keeping original in invoice-inbox:', uploadError);
            // Do NOT delete original
          } else {
            // Step 3: Verify the upload in sub-invoices — list the folder and confirm the file exists
            let verified = false;
            try {
              const { data: listData } = await supabase?.storage
                ?.from('sub-invoices')
                ?.list(`${factureProjectId}/${inv?.id}`);
              if (listData && listData?.some(f => f?.name === safeFileName)) {
                verified = true;
              } else if (uploadData?.path) {
                // Fallback: upload returned a path with no error → treat as verified
                verified = true;
              }
            } catch (verifyErr) {
              console.error('[handleFormSubmit] Verify step threw — keeping original:', verifyErr);
            }

            if (verified) {
              // Step 4: ONLY now delete the original from invoice-inbox
              const { error: deleteError } = await supabase?.storage
                ?.from('invoice-inbox')
                ?.remove([filePath]);
              if (deleteError) {
                console.warn('[handleFormSubmit] Delete of inbox original failed (non-fatal, copy already in sub-invoices):', deleteError);
              }
            } else {
              console.error('[handleFormSubmit] Verify failed — original kept in invoice-inbox. newPath:', newPath);
            }
          }
        }
      } catch (err) {
        console.error('[handleFormSubmit] File move error — original kept in invoice-inbox:', err);
      }
    }

    await linkToInvoice(selectedInboxItem?.id, inv?.id);
    const processedId = selectedInboxItem?.id;

    // Mark current item as 'created' and remove from inbox view
    const updatedItems = items?.map(i => i?.id === processedId ? { ...i, status: 'created', linkedInvoiceId: inv?.id } : i);
    // Remove created items from the displayed list (inbox only shows new + ready)
    const inboxItems = updatedItems?.filter(i => i?.status !== 'created');
    setItems(inboxItems);
    setAllItems(prev => prev?.map(i => i?.id === processedId ? { ...i, status: 'created', linkedInvoiceId: inv?.id } : i));
    setSuccessItemId(processedId);
    setTimeout(() => setSuccessItemId(null), 4000);

    // Advance to next invoice in inbox
    const nextNavigable = inboxItems?.filter(i => i?.status !== 'created');
    const currentIdx = navigableItems?.findIndex(i => i?.id === processedId);
    const nextItem = navigableItems?.[currentIdx + 1] && nextNavigable?.find(i => i?.id === navigableItems?.[currentIdx + 1]?.id)
      ? navigableItems?.[currentIdx + 1]
      : nextNavigable?.[0] || null;

    if (nextItem) {
      await handleRowClick(nextItem);
    } else {
      // No more invoices in inbox — return to list
      setSelectedInboxItem(null);
      setCurrentSignedUrl(null);
    }
  };

  // ─── Expense created from inbox item ─────────────────────────
  const handleExpenseCreated = async (expenseId) => {
    await linkToExpense(selectedInboxItem?.id, expenseId);
    const processedId = selectedInboxItem?.id;

    // Remove from displayed inbox list (status → 'created')
    const updatedItems = items?.map(i => i?.id === processedId ? { ...i, status: 'created', linkedExpenseId: expenseId } : i);
    const inboxItems = updatedItems?.filter(i => i?.status !== 'created');
    setItems(inboxItems);
    setAllItems(prev => prev?.map(i => i?.id === processedId ? { ...i, status: 'created', linked_expense_id: expenseId } : i));
    setSuccessItemId(processedId);
    setTimeout(() => setSuccessItemId(null), 4000);

    // Advance to next item in inbox
    const nextNavigable = inboxItems?.filter(i => i?.status !== 'created');
    const currentIdx = navigableItems?.findIndex(i => i?.id === processedId);
    const nextItem = navigableItems?.[currentIdx + 1] && nextNavigable?.find(i => i?.id === navigableItems?.[currentIdx + 1]?.id)
      ? navigableItems?.[currentIdx + 1]
      : nextNavigable?.[0] || null;

    if (nextItem) {
      await handleRowClick(nextItem);
    } else {
      setSelectedInboxItem(null);
      setCurrentSignedUrl(null);
    }
  };

  // ─── Inbox item deleted from duplicate modal ──────────────────
  const handleItemDeleted = () => {
    const deletedId = selectedInboxItem?.id;
    const remainingItems = items?.filter(i => i?.id !== deletedId);
    setItems(remainingItems);
    setAllItems(prev => prev?.filter(i => i?.id !== deletedId));

    // Advance to next navigable item or close panel
    const nextNavigable = remainingItems?.filter(i => i?.status !== 'created');
    const currentIdx = navigableItems?.findIndex(i => i?.id === deletedId);
    const nextItem = navigableItems?.[currentIdx + 1]
      ? nextNavigable?.find(i => i?.id === navigableItems?.[currentIdx + 1]?.id) || nextNavigable?.[0] || null
      : nextNavigable?.[0] || null;

    if (nextItem) {
      handleRowClick(nextItem);
    } else {
      setSelectedInboxItem(null);
      setCurrentSignedUrl(null);
    }
  };

  // ─── Stats computed from allItems ────────────────────────────
  const stats = {
    total: allItems?.filter(i => i?.status !== 'created')?.length,
    new: allItems?.filter(i => i?.status === 'new')?.length,
    ready: allItems?.filter(i => i?.status === 'ready')?.length,
    created: allItems?.filter(i => i?.status === 'created')?.length,
  };

  // ─── Les utilisateurs actifs de l'organisation ───────────────
  // La liste ne peut PLUS etre deduite des factures a l'ecran : depuis que la
  // RLS est resserree, elle se reduirait au seul utilisateur courant et le
  // filtre paraitrait casse. On lit donc user_profiles, via le lecteur qui
  // existe deja.
  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => {
    if (!canFilterByUploader) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await listUsers();
        if (alive) setOrgUsers((data || [])?.filter((u) => u?.is_active !== false));
      } catch (err) {
        // Degradation propre : le menu garde « Mes factures » et « Tous ».
        console.error('[invoice-inbox] org users unavailable:', err?.message || err);
      }
    })();
    return () => { alive = false; };
  }, [canFilterByUploader]);

  const uploaderOptions = useMemo(
    () => (orgUsers || [])
      ?.filter((u) => u?.id !== myId)
      ?.map((u) => ({ id: u?.id, label: u?.full_name || u?.email || u?.id })),
    [orgUsers, myId]
  );

  // ─── Split View ───────────────────────────────────────────────
  if (selectedInboxItem) {
    return (
      <div className="grid grid-cols-[60%_40%] gap-4 h-[calc(100vh-140px)]">
        {/* LEFT: PDF Viewer */}
        <div className="flex flex-col h-full overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 mb-2 bg-white border border-gray-200 rounded-xl shadow-sm flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                <Icon name="FileText" size={14} className="text-primary" />
              </div>
              <span className="text-sm font-medium text-gray-800 truncate">
                {selectedInboxItem?.fileName || t('invoiceInbox.splitView.unnamedFile')}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <StatusPill status={selectedInboxItem?.status} t={t} />
              <button
                onClick={() => { setSelectedInboxItem(null); setCurrentSignedUrl(null); }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <NativePdfViewer
              filePath={selectedInboxItem?.filePath}
              fileUrl={selectedInboxItem?.fileUrl}
              fileName={selectedInboxItem?.fileName}
              t={t}
              preloadedUrl={currentSignedUrl}
            />
          </div>
        </div>

        {/* RIGHT: Invoice Form */}
        <div className="h-full overflow-hidden">
          <div className="bg-white rounded-xl border border-gray-200 h-full overflow-y-auto">
            <InvoiceForm
              initialValues={selectedInboxInitialValues}
              onSubmit={handleFormSubmit}
              onCancel={() => { setSelectedInboxItem(null); setCurrentSignedUrl(null); }}
              fileName={selectedInboxItem?.fileName || t('invoiceInbox.splitView.unnamedFile')}
              submitLabel={t('invoiceInbox.form.createInvoice')}
              inboxItemId={selectedInboxItem?.id}
              filePath={selectedInboxItem?.filePath || null}
              inboxItem={selectedInboxItem}
              extractedData={selectedInboxItem?.extractedData || null}
              onExpenseCreated={handleExpenseCreated}
              onItemDeleted={handleItemDeleted}
              onPrev={currentNavIndex > 0 ? handleNavPrev : null}
              onNext={currentNavIndex < navigableItems?.length - 1 ? handleNavNext : null}
              currentIndex={currentNavIndex >= 0 ? currentNavIndex + 1 : null}
              totalNavigable={navigableItems?.length}
              remainingCount={remainingCount}
            />
          </div>
        </div>
      </div>
    );
  }

  // ─── List View ────────────────────────────────────────────────
  return (
    <div className="pb-24">
      {/* Page Header: title left, cards right */}
      <div className="flex items-start justify-between gap-6 mb-6">
        {/* Left: Title + Subtitle */}
        <div className="flex-shrink-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Icon name="Inbox" size={20} className="text-primary" />
            <h1 className="text-lg font-semibold text-[#2C3E50]">{t('invoiceInbox.title')}</h1>
          </div>
          <p className="text-xs font-normal text-[#6B7280]">{t('invoiceInbox.subtitle')}</p>
        </div>

        {/* Right: KPI Cards */}
        <div className="flex items-center gap-2">
          <StatsCard
            title={t('invoiceInbox.filter.new')}
            value={stats?.new}
            iconName="FilePlus"
            iconColor="text-blue-500"
            valueColor="text-blue-500"
            titleWeight="font-medium"
            sublabel=""
          />
          <StatsCard
            title={t('invoiceInbox.filter.ready')}
            value={stats?.ready}
            iconName="Sparkles"
            iconColor="text-purple-500"
            valueColor="text-purple-600"
            titleWeight="font-medium"
            sublabel=""
          />
          <StatsCard
            title={t('invoiceInbox.filter.created')}
            value={stats?.created}
            iconName="CheckCircle"
            iconColor="text-green-500"
            valueColor="text-green-600"
            titleWeight="font-medium"
            sublabel=""
          />
        </div>
      </div>
      {/* Upload Progress Banner */}
      {uploadProgress !== null && (
        <UploadProgressBanner progress={uploadProgress} rejections={uploadRejections} onDismiss={() => setUploadProgress(null)} />
      )}
      {/* Filter Bar — Directory style */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Icon name="Filter" size={18} className="text-gray-700" />
            <h2 className="text-sm font-semibold text-gray-900">{t('invoiceInbox.filter.title') || 'Filter Invoices'}</h2>
            <span className="text-xs text-primary font-medium">
              {items?.length} {t('invoiceInbox.filter.invoicesLabel') || 'Invoices'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {/* Upload Invoice button */}
            <button
              onClick={() => fileInputRef?.current?.click()}
              disabled={uploadProgress !== null && uploadProgress?.current < uploadProgress?.total}
              className="bg-primary text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-primary-dark transition-colors disabled:opacity-60 flex items-center gap-1.5 flex-shrink-0"
            >
              <Icon name="Upload" size={14} />
              {uploadProgress !== null && uploadProgress?.current < uploadProgress?.total
                ? t('invoiceInbox.uploading')
                : t('invoiceInbox.uploadButton')}
            </button>
            {/* Clear Filters */}
            {(search || filterStatus !== 'inbox' || filterUploader !== 'me') && (
              <button
                onClick={() => { setSearch(''); setFilterStatus('inbox'); setFilterUploader('me'); }}
                className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Icon name="X" size={14} />
                {t('directory.filter.clearFilters') || 'Clear Filters'}
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('invoiceInbox.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
              className="h-9 w-full rounded-md border border-gray-200 bg-white pl-8 pr-3 py-1.5 text-xs text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
          </div>

          {/* Status custom dropdown */}
          <div className="relative" ref={statusDropRef}>
            <button
              onClick={() => { setStatusDropOpen(prev => !prev); setUploaderDropOpen(false); }}
              className={`h-9 w-full rounded-lg border bg-white px-3 py-1.5 text-xs text-gray-900 flex items-center justify-between hover:bg-gray-50 transition-colors ${statusDropOpen ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200'}`}
            >
              <span className="truncate">
                {filterStatus === 'inbox' ? t('invoiceInbox.filter.inbox') : filterStatus === 'new' ? t('invoiceInbox.filter.new') : filterStatus === 'ready' ? t('invoiceInbox.filter.ready') : t('invoiceInbox.filter.created')}
              </span>
              <Icon name="ChevronDown" size={14} className={`text-gray-400 flex-shrink-0 ml-1 transition-transform duration-150 ${statusDropOpen ? 'rotate-180' : ''}`} />
            </button>
            {statusDropOpen && (
              <div className="absolute top-full left-0 right-0 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                {[
                  { value: 'inbox', label: t('invoiceInbox.filter.inbox') },
                  { value: 'ready', label: t('invoiceInbox.filter.ready') },
                  { value: 'created', label: t('invoiceInbox.filter.created') },
                ]?.map(opt => (
                  <button
                    key={opt?.value}
                    onClick={() => { setFilterStatus(opt?.value); setStatusDropOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-xs transition-colors flex items-center justify-between ${filterStatus === opt?.value ? 'bg-orange-50 text-primary font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <span>{opt?.label}</span>
                    {filterStatus === opt?.value && <Icon name="Check" size={13} className="text-primary flex-shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Filtre par televerseur — administrateur et adjoint SEULEMENT.
              Pour les autres roles il n'est pas desactive : il n'existe pas.
              Un menu grise, ou reduit a son propre nom, poserait une question
              a laquelle ils n'ont pas a repondre. */}
          {canFilterByUploader && (
          <div className="relative" ref={uploaderDropRef}>
            <button
              onClick={() => { setUploaderDropOpen(prev => !prev); setStatusDropOpen(false); }}
              className={`h-9 w-full rounded-lg border bg-white px-3 py-1.5 text-xs text-gray-900 flex items-center justify-between hover:bg-gray-50 transition-colors ${uploaderDropOpen ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200'}`}
            >
              <span className="truncate">
                {filterUploader === 'me'
                  ? t('invoiceInbox.filter.uploaderMine')
                  : filterUploader === 'all'
                    ? t('invoiceInbox.filter.uploaderAll')
                    : uploaderOptions?.find(u => u?.id === filterUploader)?.label || t('invoiceInbox.filter.uploaderMine')}
              </span>
              <Icon name="ChevronDown" size={14} className={`text-gray-400 flex-shrink-0 ml-1 transition-transform duration-150 ${uploaderDropOpen ? 'rotate-180' : ''}`} />
            </button>
            {uploaderDropOpen && (
              <div className="absolute top-full left-0 right-0 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden max-h-72 overflow-y-auto">
                <button
                  onClick={() => { setFilterUploader('me'); setUploaderDropOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-xs transition-colors flex items-center justify-between ${filterUploader === 'me' ? 'bg-orange-50 text-primary font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <span>{t('invoiceInbox.filter.uploaderMine')}</span>
                  {filterUploader === 'me' && <Icon name="Check" size={13} className="text-primary flex-shrink-0" />}
                </button>
                <button
                  onClick={() => { setFilterUploader('all'); setUploaderDropOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-xs transition-colors flex items-center justify-between ${filterUploader === 'all' ? 'bg-orange-50 text-primary font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  <span>{t('invoiceInbox.filter.uploaderAll')}</span>
                  {filterUploader === 'all' && <Icon name="Check" size={13} className="text-primary flex-shrink-0" />}
                </button>
                {uploaderOptions?.map(u => (
                  <button
                    key={u?.id}
                    onClick={() => { setFilterUploader(u?.id); setUploaderDropOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-xs transition-colors flex items-center justify-between ${filterUploader === u?.id ? 'bg-orange-50 text-primary font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <span>{u?.label}</span>
                    {filterUploader === u?.id && <Icon name="Check" size={13} className="text-primary flex-shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      </div>
      <input ref={fileInputRef} type="file" accept="application/pdf,image/jpeg,image/png" multiple className="hidden" onChange={handleFileInput} />
      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : items?.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-lg border border-gray-200 shadow-sm">
          <Icon name="Inbox" size={48} className="mx-auto mb-4 text-[#6B7280]" />
          <p className="text-[#6B7280]">{t('invoiceInbox.noInvoicesTitle')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto pb-1">
          {/* Les colonnes ont des largeurs fixes : sous ~894px la ligne debordait
              la page sans rien pour la rattraper. Le defilement horizontal ferme ca. */}
          <div className="space-y-2 min-w-[894px]">
          {/* Table Header */}
          <div className="flex items-center h-12">
            {/* Select All Checkbox */}
            <div className="px-3 flex items-center justify-center flex-shrink-0" style={{ width: '44px' }}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={el => { if (el) el.indeterminate = isIndeterminate; }}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer accent-primary"
                title={allSelected ? 'Deselect all' : 'Select all'}
              />
            </div>
            <div className="px-4 text-xs font-semibold text-foreground/80" style={{ minWidth: '200px', flex: 1 }}>
              {t('invoiceInbox.columns.file')}
            </div>
            <div className="px-4 text-xs font-semibold text-foreground/80" style={{ width: '180px', flexShrink: 0 }}>
              {t('invoiceInbox.columns.supplier')}
            </div>
            <div className="px-4 text-xs font-semibold text-foreground/80" style={{ width: '140px', flexShrink: 0 }}>
              {t('invoiceInbox.columns.date')}
            </div>
            <div className="px-4 text-xs font-semibold text-foreground/80" style={{ width: '140px', flexShrink: 0 }}>
              {t('invoiceInbox.columns.amount')}
            </div>
            <div className="px-4 text-xs font-semibold text-foreground/80" style={{ width: '100px', flexShrink: 0 }}>
              {t('invoiceInbox.columns.status')}
            </div>
            {/* Actions — en-tete volontairement vide */}
            <div className="px-2" style={{ width: '90px', flexShrink: 0 }} />
          </div>

          {/* Row Cards */}
          {items?.map((item) => {
            const isSelected = selectedIds?.has(item?.id);
            const rowScanState = scanState?.get(item?.id);
            const isScanning = rowScanState === 'scanning';
            const scanError = rowScanState === 'error' ? (item?.aiLastError || 'Extraction echouee') : null;
            const isProcessed = item?.status === 'created';
            const isOpening = openingItemId === item?.id;
            const analyzed = isAnalyzed(item);
            const attempts = item?.aiExtractionAttempts || 0;
            const isRetrying = retryingIds?.has(item?.id);

            return (
              <div
                key={item?.id}
                onClick={() => { if (isScanning) return; handleRowClick(item); }}
                className={`flex items-center h-10 bg-white border rounded-lg shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_0_rgba(0,0,0,0.02)] transition-all duration-200 ${
                  isProcessed ? 'opacity-60 cursor-default border-gray-200' : isScanning ? 'cursor-not-allowed opacity-75 border-purple-200 bg-purple-50/30' : 'cursor-pointer border-gray-200 hover:bg-gray-100/60 hover:border-primary hover:border-2'
                } ${successItemId === item?.id ? 'bg-green-50 border-green-200' : ''} ${isSelected ? 'bg-orange-50/40 border-primary/40' : ''} ${isOpening ? 'bg-orange-50/60 border-primary/60' : ''}`}
              >
                {/* Row Checkbox */}
                <div
                  className="px-3 flex items-center justify-center flex-shrink-0"
                  style={{ width: '44px' }}
                  onClick={(e) => e?.stopPropagation()}
                >
                  {!isProcessed && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isScanning}
                      onChange={(e) => { if (!isScanning) toggleSelectItem(e, item?.id); }}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer accent-primary disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                  )}
                </div>
                {/* File */}
                <div className="px-4 flex items-center gap-2 min-w-0" style={{ minWidth: '200px', flex: 1 }}>
                  <div className="w-6 h-6 rounded-md bg-orange-50 flex items-center justify-center flex-shrink-0">
                    {isOpening ? (
                      <Icon name="Loader2" size={13} className="text-primary animate-spin" />
                    ) : (
                      <Icon name="FileText" size={13} className="text-primary" />
                    )}
                  </div>
                  <span className="text-xs text-foreground truncate">
                    {item?.fileName || t('invoiceInbox.splitView.unnamedFile')}
                  </span>
                  {item?.source === 'email' && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-600 text-[10px] font-medium flex-shrink-0">
                      <Icon name="Mail" size={10} className="flex-shrink-0" />
                      {item?.senderEmail
                        ? <span className="truncate max-w-[100px]">{item?.senderEmail}</span>
                        : 'Email'}
                    </span>
                  )}
                  {item?.uploader && item?.source !== 'email' && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-medium flex-shrink-0">
                      <span className="w-3.5 h-3.5 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center text-[8px] font-bold uppercase leading-none flex-shrink-0">
                        {(item?.uploader?.full_name || item?.uploader?.email || '?')?.[0]?.toUpperCase()}
                      </span>
                      <span className="truncate max-w-[80px]">
                        {item?.uploader?.full_name || item?.uploader?.email?.split('@')?.[0] || 'Unknown'}
                      </span>
                    </span>
                  )}
                </div>
                {/* Supplier */}
                <div className="px-4 text-xs text-foreground truncate" style={{ width: '180px', flexShrink: 0 }}>
                  {getExtractedField(item, 'supplier')}
                </div>
                {/* Date */}
                <div className="px-4 text-xs text-foreground" style={{ width: '140px', flexShrink: 0 }}>
                  {getExtractedField(item, 'date') !== '—'
                    ? getExtractedField(item, 'date')
                    : formatDate(item?.createdAt)}
                </div>
                {/* Amount */}
                <div className="px-4 text-xs text-foreground font-medium" style={{ width: '140px', flexShrink: 0 }}>
                  {extractedAmount(item) != null ? fmtCAD(extractedAmount(item)) : '—'}
                </div>
                {/* Status + Scan badge */}
                <div className="px-4 flex items-center gap-2" style={{ width: '100px', flexShrink: 0 }}>
                  {isOpening ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-600">
                      <Icon name="Loader2" size={11} className="animate-spin" />
                      Opening…
                    </span>
                  ) : isScanning ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-600">
                      <Icon name="Loader2" size={11} className="animate-spin" />
                      Scanning…
                    </span>
                  ) : analyzed ? (
                    // isAnalyzed, jamais status : une facture status='ready' avec
                    // aiProcessed=false ne doit pas se dire analysee.
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-green-700 text-[11px] font-medium">
                      <Icon name="Sparkles" size={11} className="flex-shrink-0" />
                      Analysé
                    </span>
                  ) : scanError ? (
                    <span
                      title={scanError}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 cursor-help"
                    >
                      <Icon name="AlertTriangle" size={11} />
                      Echec IA
                    </span>
                  ) : (
                    <StatusPill status={item?.status} t={t} />
                  )}
                  {successItemId === item?.id && (
                    <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                      <Icon name="CheckCircle" size={12} />
                      {t('invoiceInbox.invoiceCreated')}
                    </span>
                  )}
                </div>
                {/* Actions */}
                <div
                  className="px-2 flex items-center justify-end"
                  style={{ width: '90px', flexShrink: 0 }}
                  onClick={(e) => e?.stopPropagation()}
                >
                  {!isProcessed && (() => {
                    // Ce bouton est le seul chemin de relance qui reste. Une facture
                    // deja analysee reste cliquable, mais passe par une confirmation :
                    // un second appel payant doit etre voulu, jamais accidentel.
                    const disabled = attempts >= 3 || isRetrying || isScanning;
                    const title = attempts >= 3
                      ? (item?.aiLastError || 'Max retries reached')
                      : isRetrying || isScanning
                        ? 'Extraction en cours...'
                        : analyzed
                          ? 'Déjà analysée — relancer l\'analyse'
                          : 'Analyser cette facture avec l\'IA';
                    return (
                      <button
                        onClick={(e) => {
                          if (disabled) return;
                          if (analyzed) {
                            e?.stopPropagation();
                            setRescanConfirmItem(item);
                            return;
                          }
                          handleRetryExtraction(e, item);
                        }}
                        disabled={disabled}
                        title={title}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors border ${
                          disabled
                            ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100 cursor-pointer'
                        }`}
                      >
                        <Icon
                          name={isRetrying || isScanning ? 'Loader2' : 'Sparkles'}
                          size={11}
                          className={`flex-shrink-0 ${isRetrying || isScanning ? 'animate-spin' : ''}`}
                        />
                        Scan IA
                      </button>
                    );
                  })()}
                </div>
              </div>
            );
          })}

          {/* Footer count */}
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {items?.length === 1
                ? t('invoiceInbox.invoiceCount')?.replace('{count}', items?.length)
                : t('invoiceInbox.invoiceCountPlural')?.replace('{count}', items?.length)}
            </span>
          </div>
          </div>
        </div>
      )}
      {/* Floating Bulk Action Bar */}
      <BulkActionBar
        count={selectedIds?.size}
        actions={[
          {
            key: 'scan',
            label: scanningIds?.size > 0
              ? t('bulk.scanning', { count: scanningIds?.size })
              : t('bulk.scan'),
            onClick: handleBulkScan,
            disabled: scanningIds?.size > 0,
            // Bouton sur mesure : on garde le violet plein et l'icone IA en degrade.
            render: () => (
              <button
                onClick={handleBulkScan}
                disabled={scanningIds?.size > 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg disabled:opacity-60 text-white text-[11px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
                style={{ backgroundColor: '#7C3AED' }}
                onMouseEnter={e => { if (!(scanningIds?.size > 0)) e.currentTarget.style.backgroundColor = '#6D28D9'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#7C3AED'; }}
              >
                {scanningIds?.size > 0 ? (
                  <Icon name="Loader2" size={13} className="animate-spin" />
                ) : (
              <svg width="18" height="18" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
              <linearGradient id="inboxRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22D3EE" />
              <stop offset="50%" stopColor="#818CF8" />
              <stop offset="100%" stopColor="#E879F9" />
              </linearGradient>
              <linearGradient id="inboxStarGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22D3EE" />
              <stop offset="100%" stopColor="#A78BFA" />
              </linearGradient>
              <linearGradient id="inboxStarGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#818CF8" />
              <stop offset="100%" stopColor="#E879F9" />
              </linearGradient>
              </defs>
              {/* Circular gradient ring — open at top-right */}
              <circle cx="50" cy="50" r="42" stroke="url(#inboxRingGrad)" strokeWidth="7" fill="none" />
              {/* Large 4-pointed star — center-left, bigger */}
              <path d="M40 18 C40 18 43 36 48 42 C53 48 71 51 71 51 C71 51 53 54 48 60 C43 66 40 84 40 84 C40 84 37 66 32 60 C27 54 9 51 9 51 C9 51 27 48 32 42 C37 36 40 18 40 18 Z" fill="url(#inboxStarGrad1)" />
              {/* Small 4-pointed star — top right */}
              <path d="M72 22 C72 22 73.2 29 75 31 C76.8 33 83 34 83 34 C83 34 76.8 33 75 31 C73.2 29 72 22 72 22 Z" fill="url(#inboxStarGrad2)" />
              </svg>
                )}
                {scanningIds?.size > 0
                  ? t('bulk.scanning', { count: scanningIds?.size })
                  : t('bulk.scan')}
              </button>
            ),
          },
          {
            key: 'delete',
            label: t('bulk.delete'),
            icon: 'Trash2',
            variant: 'danger',
            disabled: scanningIds?.size > 0,
            onClick: () => setShowDeleteConfirm(true),
          },
        ]}
        onClear={() => setSelectedIds(new Set())}
        clearLabel={t('bulk.clear')}
      />
      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title={`Delete ${selectedIds?.size} invoice${selectedIds?.size !== 1 ? 's' : ''}?`}
          message="This will permanently remove the selected files and their records."
          confirmVariant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={handleBulkDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          loading={bulkDeleting}
        />
      )}

      {/* Relance d'une facture deja analysee — le seul appel payant volontaire */}
      {rescanConfirmItem && (
        <ConfirmDialog
          isOpen={true}
          title="Relancer l'analyse ?"
          icon="Sparkles"
          message={
            // Les factures analysees avant juillet 2026 n'ont pas ai_extracted_at :
            // pas de date plutot qu'une date invalide.
            rescanConfirmItem?.aiExtractedAt
              ? `Cette facture a déjà été analysée le ${formatLocaleDate(rescanConfirmItem?.aiExtractedAt, language)}. Relancer va consommer une nouvelle analyse.`
              : 'Cette facture a déjà été analysée. Relancer va consommer une nouvelle analyse.'
          }
          confirmVariant="primary"
          confirmLabel="Relancer"
          cancelLabel="Annuler"
          onConfirm={() => {
            const target = rescanConfirmItem;
            setRescanConfirmItem(null);
            handleRetryExtraction(null, target);
          }}
          onCancel={() => setRescanConfirmItem(null)}
        />
      )}

      {/* Echec de suppression — reste a l'ecran jusqu'a fermeture manuelle :
          une suppression refusee ne doit pas disparaitre toute seule. */}
      {deleteError && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white text-xs font-medium px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-2 max-w-[90vw]">
          <Icon name="AlertCircle" size={14} className="flex-shrink-0" />
          <span>{deleteError}</span>
          <button
            type="button"
            onClick={() => setDeleteError(null)}
            className="ml-1 p-0.5 rounded hover:bg-white/20 transition-colors flex-shrink-0"
          >
            <Icon name="X" size={12} />
          </button>
        </div>
      )}

      {/* Retry Toast */}
      {retryToast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 text-white text-xs font-medium px-4 py-2.5 rounded-xl shadow-lg flex items-start gap-2 max-w-[560px] ${
          retryToast?.type === 'error' ? 'bg-red-600' : retryToast?.type === 'success' ? 'bg-green-600' : 'bg-gray-900'
        }`}>
          <Icon
            name={retryToast?.type === 'error' ? 'AlertTriangle' : retryToast?.type === 'success' ? 'CheckCircle' : 'Loader2'}
            size={13}
            className={`flex-shrink-0 mt-px ${retryToast?.type === 'loading' ? 'animate-spin text-primary' : ''}`}
          />
          <span className="break-words">{retryToast?.message}</span>
        </div>
      )}
    </div>
  );
};

export default InvoiceInboxPage;
